/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import {
  DataGridPro,
  GridCallbackDetails,
  GridColDef,
  GridEventListener,
  GridFeatureMode,
  GridPinnedColumns,
  GridRowIdGetter,
  GridRowParams,
  GridRowSelectionModel,
  GridSlotsComponent,
  GridSortDirection,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { Box, IconButton, SxProps, useTheme } from "@mui/material";
import { useStoreDispatch } from "../../../store/hooks";
import { setPaginationModel } from "../../../store/helpers/helperReducers";
import { useSelector } from "react-redux";
import { paginationModelSelector } from "../../../store/helpers/helperSelectors";
import { GridSlotsComponentsProps, UncapitalizeObjectKeys } from "@mui/x-data-grid/internals";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { iconButtonSxProps } from "../../helpers";
import { IPagination } from "../../../store/helpers/types";
import { initialState } from "../../../store/helpers/helperInitialState";

/* ── Custom checkbox icons matching shadcn/Radix Checkbox (h-4 w-4, rounded-sm, border-primary, lucide Check stroke) ── */
const ShadcnUncheckedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="15" height="15" rx="2" stroke="currentColor" strokeWidth="1" fill="none" />
  </svg>
);

const ShadcnCheckedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="0" width="16" height="16" rx="2.5" fill="currentColor" />
    <path d="M12 5L6.5 10.5L4 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const ShadcnIndeterminateIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="0" width="16" height="16" rx="2.5" fill="currentColor" />
    <path d="M4 8H12" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" />
  </svg>
);

interface ICustomDataGridProps {
  tableId: string;
  sx?: SxProps;
  columns: GridColDef<any>[];
  rows: any[];
  rowCount?: number | undefined;
  loading?: boolean | undefined;
  autoHeight?: boolean | undefined;
  checkboxSelection?: boolean | undefined;
  onRowClick?: GridEventListener<"rowClick"> | undefined;
  sortingOrder?: GridSortDirection[] | undefined;
  sortModel?: GridSortModel | undefined;
  onSortModelChange?: ((model: GridSortModel, details: GridCallbackDetails<any>) => void) | undefined;
  getRowId?: GridRowIdGetter<any> | undefined;
  disableRowSelectionOnClick?: boolean | undefined;
  slots?: UncapitalizeObjectKeys<Partial<GridSlotsComponent>> | undefined;
  slotProps?: GridSlotsComponentsProps | undefined;
  disableColumnMenu?: boolean | undefined;
  rowSelectionModel?: any[];
  onRowSelectionModelChange?: ((rowSelectionModel: GridRowSelectionModel, details: GridCallbackDetails<any>) => void) | undefined;
  hideFooter?: boolean | undefined;
  pinnedColumns?: GridPinnedColumns | undefined;
  paginationMode?: GridFeatureMode | undefined;
  sortingMode?: GridFeatureMode | undefined;
  initialSortModel?: GridSortModel;
  onRowDoubleClick?: GridEventListener<"rowDoubleClick"> | undefined;
  isRowSelectable?: ((params: GridRowParams<any>) => boolean) | undefined;
  disableMultipleRowSelection?: boolean | undefined;
  onCellClick?: GridEventListener<"cellClick"> | undefined;
  getRowClassName?: ((params: GridRowParams<any>) => string) | undefined;
  keepNonExistentRowsSelected?: boolean;
  pagination?: boolean;
  fillContainer?: boolean;
  allowVerticalScroll?: boolean;
}

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page" sx={iconButtonSxProps}>
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page" sx={iconButtonSxProps}>
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton onClick={handleNextButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page" sx={iconButtonSxProps}>
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
        sx={iconButtonSxProps}
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

// ── Grid dimension constants ──
const ROW_HEIGHT = 52; // MUI DataGrid default row height
const HEADER_HEIGHT = 52;
const FOOTER_HEIGHT = 48;

const getGridHeight = (rowCount: number, pageSize: number, hideFooter: boolean): number => {
  let visibleRows = rowCount <= 5 ? 5 : Math.min(rowCount, pageSize);
  return HEADER_HEIGHT + visibleRows * ROW_HEIGHT + (hideFooter ? 0 : FOOTER_HEIGHT);
};

const CustomDataGrid: React.FC<ICustomDataGridProps> = ({
  tableId = "customTable",
  sx,
  rows,
  columns,
  rowCount,
  loading,
  sortingOrder,
  sortModel,
  getRowId,
  onRowClick,
  checkboxSelection,
  //pageSizeOptions,
  onSortModelChange,
  disableRowSelectionOnClick,
  slots,
  slotProps,
  autoHeight,
  disableColumnMenu,
  rowSelectionModel,
  onRowSelectionModelChange,
  hideFooter = false,
  pinnedColumns,
  paginationMode, // = "server",
  sortingMode, // = "server",
  initialSortModel,
  onRowDoubleClick,
  isRowSelectable,
  disableMultipleRowSelection = false,
  onCellClick,
  getRowClassName,
  keepNonExistentRowsSelected = false,
  pagination = true,
  fillContainer = false,
  allowVerticalScroll = false,
}) => {
  const dispatch = useStoreDispatch();

  const paginationModel = useSelector(paginationModelSelector);

  // ============================================================================
  // IMPORTANT: Text Selection Feature - DO NOT REMOVE
  // This allows users to highlight/copy text from grid cells without triggering
  // row/cell click events. This feature has been removed multiple times by
  // accident - please preserve these handlers when making changes.
  // ============================================================================

  // Helper to check if user is selecting text
  const isTextSelected = (): boolean => {
    const selection = window.getSelection();
    return selection ? selection.toString().length > 0 : false;
  };

  // Wrap row click handler to prevent triggering when text is being selected
  const handleRowClick: GridEventListener<"rowClick"> | undefined = onRowClick
    ? (params, event, details) => {
      if (!isTextSelected()) {
        onRowClick(params, event, details);
      }
    }
    : undefined;

  // Wrap cell click handler to prevent triggering when text is being selected
  const handleCellClick: GridEventListener<"cellClick"> | undefined = onCellClick
    ? (params, event, details) => {
      if (!isTextSelected()) {
        onCellClick(params, event, details);
      }
    }
    : undefined;

  //  Wrap double click handler to prevent triggering when text is being selected
  const handleRowDoubleClick: GridEventListener<"rowDoubleClick"> | undefined = onRowDoubleClick
    ? (params, event, details) => {
      if (!isTextSelected()) {
        onRowDoubleClick(params, event, details);
      }
    }
    : undefined;

  // const handleSortModelChange = React.useCallback(
  //   (sortModel: GridSortModel, details: GridCallbackDetails<any>) => {
  //     console.log('details :>> ', details);
  //     if (sortModel.length > 0) {
  //       const sortedField: string = Object.values(sortModel[0]).toString();
  //       dispatch(setDataGridSort({ [tableId]: sortedField }));
  //     }
  //   },
  //   [dispatch, tableId]
  // );

  const dataGridSxProps: SxProps = {
    /* ── Outer shell: no border by default (border moves to MuiDataGrid-main when toolbar is present) ── */
    border: "1px solid var(--border)",
    borderRadius: "var(--radius)",
    overflow: "hidden",
    backgroundColor: "var(--card)",
    color: "var(--foreground)",
    fontSize: "var(--font-size-sm)",

    /* ── Overlay ── */
    "& .MuiDataGrid-overlayWrapper": { minHeight: 55 },
    "& .MuiDataGrid-overlay": { backgroundColor: "var(--card)", color: "var(--muted-foreground)" },

    /* ── Rows: border-b hover:bg-muted, last-child border-transparent ── */
    "& .MuiDataGrid-row": {
      borderBottom: "1px solid var(--border)",
      fontSize: "var(--font-size-sm)",
      transition: "background-color 150ms cubic-bezier(0.4,0,0.2,1)",
      "&:hover": {
        backgroundColor: "var(--muted) !important",
      },
      "&.Mui-selected": {
        backgroundColor: "var(--muted) !important",
        "&:hover": {
          backgroundColor: "var(--muted) !important",
        },
      },
      "&:last-child": {
        borderBottom: "none",
      },
    },

    /* ── Cells: px-4 align-middle ── */
    "& .MuiDataGrid-cell": {
      borderBottom: "none",
      cursor: onRowClick || onRowDoubleClick ? "pointer" : "",
      color: "var(--foreground)",
      fontSize: "var(--font-size-sm)",
      padding: "0 16px",
    },

    "& .MuiDataGrid-cell .MuiDataGrid-cellContent": {
      color: "inherit",
      fontSize: "inherit",
    },
    "& .MuiDataGrid-cell .MuiTypography-root": {
      color: "inherit",
      fontSize: "inherit",
    },
    "& .MuiDataGrid-cell .MuiChip-root": {
      fontSize: "var(--font-size-xs)",
    },

    /* ── Column headers: h-12 font-medium text-muted-foreground, no hover bg ── */
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: "var(--font-weight-medium, 500)",
      fontSize: "var(--font-size-xs)",
      color: "var(--muted-foreground)",
      textTransform: "none",
      letterSpacing: "0.01em",
    },
    "& .MuiDataGrid-columnHeaders": {
      borderBottom: "1px solid var(--border)",
      backgroundColor: "var(--card)",
      color: "var(--muted-foreground)",
      fontSize: "var(--font-size-xs)",
      minHeight: "52px !important",
      maxHeight: "52px !important",
    },
    "& .MuiDataGrid-columnHeader": {
      "&:hover": { backgroundColor: "transparent !important" },
      "&:focus": { outline: "none" },
      "&:focus-within": { outline: "none" },
    },
    "& .MuiDataGrid-columnSeparator": {
      color: "var(--border)",
      opacity: "0 !important",
      transition: "opacity 150ms ease",
      "& .MuiDataGrid-iconSeparator": { fontSize: "16px" },
      "&:hover, &.MuiDataGrid-columnSeparator--resizing": {
        opacity: "1 !important",
        color: "var(--primary)",
      },
    },
    "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator": {
      opacity: "1 !important",
    },

    /* ── Footer: border-t ── */
    "& .MuiDataGrid-footerContainer": {
      borderTop: "1px solid var(--border)",
      backgroundColor: "var(--card)",
      color: "var(--muted-foreground)",
      minHeight: "48px",
    },

    /* ── Pagination ── */
    "& .MuiTablePagination-root": {
      color: "var(--muted-foreground)",
      fontSize: "var(--font-size-sm)",
    },
    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
      fontSize: "var(--font-size-sm)",
      color: "var(--muted-foreground)",
    },
    "& .MuiTablePagination-select": {
      fontSize: "var(--font-size-sm)",
      color: "var(--foreground)",
    },
    "& .MuiTablePagination-selectIcon": { color: "var(--muted-foreground)" },

    /* ── Icon buttons ── */
    "& .MuiIconButton-root": {
      color: "var(--muted-foreground)",
      "&:hover": { backgroundColor: "var(--accent)" },
      "&.Mui-disabled": { color: "var(--muted-foreground)", opacity: 0.4 },
    },

    /* ── Toolbar: sits outside the bordered box, no border of its own ── */
    "& .MuiDataGrid-toolbarContainer": {
      padding: "8px",
      borderBottom: "none",
    },
    /* When toolbar is present, strip outer border from shell and put it on the grid body instead */
    "&:has(.MuiDataGrid-toolbarContainer)": {
      border: "none",
      borderRadius: 0,
      overflow: "visible",
    },
    "& .MuiDataGrid-toolbarContainer ~ .MuiDataGrid-main": {
      border: "1px solid var(--border)",
      borderRadius: "var(--radius)",
      overflow: "hidden",
    },
    /* Footer also needs border when toolbar is present (it sits outside .MuiDataGrid-main) */
    "& .MuiDataGrid-toolbarContainer ~ .MuiDataGrid-footerContainer": {
      borderTop: "none",
    },

    /* ── Sort & menu icons ── */
    "& .MuiDataGrid-sortIcon": { color: "var(--muted-foreground)" },
    "& .MuiDataGrid-menuIconButton": { color: "var(--muted-foreground)" },

    /* ── Scroller ── */
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: "var(--card)",
      ...(fillContainer && !allowVerticalScroll && { overflowY: "hidden" }),
    },

    /* ── Checkbox (matches shadcn Checkbox: h-4 w-4, rounded-sm, border-primary, shadow) ── */
    "& .MuiCheckbox-root": {
      color: "var(--primary)",
      padding: "4px",
      filter: "drop-shadow(0 1px 1px rgb(0 0 0 / 0.05))",
      "& svg": { width: 16, height: 16 },
      "&:hover": { backgroundColor: "transparent" },
      "&.Mui-checked": { color: "var(--primary)" },
      "&.MuiCheckbox-indeterminate": { color: "var(--primary)" },
    },
    /* Dark-mode: unchecked checkbox uses muted-foreground border (matches shadcn dark:border-muted-foreground) */
    ".dark & .MuiCheckbox-root:not(.Mui-checked):not(.MuiCheckbox-indeterminate)": {
      color: "var(--muted-foreground)",
    },
    /* ── Checkbox column: compact 40px width (matches AMS select column size: 40) ── */
    "& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox": {
      minWidth: "40px !important",
      maxWidth: "40px !important",
      width: "40px !important",
    },

    /* ── Pinned columns ── */
    "--DataGrid-pinnedBackground": "var(--card)",
    "& .MuiDataGrid-pinnedColumns": { backgroundColor: "var(--card)" },
    "& .MuiDataGrid-pinnedColumnHeaders": { backgroundColor: "var(--card)" },
    "& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight": { backgroundColor: "var(--card)" },
    "& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight": { backgroundColor: "var(--muted)" },

    /* ── Scrollbars ── */
    ...(fillContainer && !allowVerticalScroll && { "& .MuiDataGrid-scrollbar--vertical": { display: "none" } }),
    "& .MuiDataGrid-scrollbar, & .MuiDataGrid-scrollbar--horizontal": {
      backgroundColor: "var(--card)",
      height: "8px !important",
      opacity: "1 !important",
    },
    "& .MuiDataGrid-filler": { backgroundColor: "var(--card)" },
    "& .MuiDataGrid-scrollbarFiller": { backgroundColor: "var(--card)" },
    "&::-webkit-scrollbar": { backgroundColor: "var(--card)", height: "10px", width: "6px" },
    "&::-webkit-scrollbar-thumb": { backgroundColor: "var(--border)", borderRadius: "5px" },
    "&::-webkit-scrollbar-corner": { backgroundColor: "var(--card)" },
    "& *::-webkit-scrollbar": { backgroundColor: "var(--card)", height: "10px", width: "6px" },
    "& *::-webkit-scrollbar-thumb": { backgroundColor: "var(--border)", borderRadius: "5px" },
    "& *::-webkit-scrollbar-corner": { backgroundColor: "var(--card)" },
  };
  const columnMenuProps: SxProps = {
    basePopper: {
      sx: {
        "& .MuiPaper-root": {
          backgroundColor: "var(--popover)",
          color: "var(--popover-foreground)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
        },
        "& .MuiListItemIcon-root": { color: "inherit" },
        "& .MuiMenuItem-root": {
          fontSize: "var(--font-size-sm)",
          "&:hover": { backgroundColor: "var(--accent)" },
        },
      },
    },
  };
  const currentPageSize = paginationModel[tableId]?.pageSize || initialState.pagination.pageSize;
  const actualRows = rows?.length || 0;
 
  const computedHeight = getGridHeight(actualRows,currentPageSize, hideFooter);

  // For fillContainer: height based on actual rows displayed (not pageSize)
  const actualRowsHeight = HEADER_HEIGHT + Math.min(actualRows, currentPageSize) * ROW_HEIGHT + (hideFooter ? 0 : FOOTER_HEIGHT);

  // Minimum height guarantees space for default page size (10 rows), not the user-selected page size
  const defaultPageSize = initialState.pagination.pageSize;
  const minRowsHeight = HEADER_HEIGHT + defaultPageSize * ROW_HEIGHT + (hideFooter ? 0 : FOOTER_HEIGHT);

  // Measure available viewport space for fillContainer mode
  const gridWrapperRef = React.useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = React.useState(0);

  React.useEffect(() => {
    if (!fillContainer) return;
    const measure = () => {
      if (gridWrapperRef.current) {
        const rect = gridWrapperRef.current.getBoundingClientRect();
        setAvailableHeight(window.innerHeight - rect.top - 16);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [fillContainer]);

  const gridHeight = fillContainer
    ? Math.max(availableHeight, actualRowsHeight, minRowsHeight)
    : computedHeight;

  return (
    <div ref={gridWrapperRef}>
      <DataGridPro
        sx={{ height: gridHeight, ...dataGridSxProps, ...sx }}
        pagination={pagination}
        columnHeaderHeight={HEADER_HEIGHT}
        rows={rows}
        columns={columns}
        rowCount={rowCount}
        loading={loading}
        getRowId={getRowId}
        autoHeight={autoHeight}
        disableColumnMenu={disableColumnMenu}
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        pageSizeOptions={pagination ? [10, 20, 50, 100] : undefined}
        paginationModel={pagination ? paginationModel[tableId] : undefined}
        paginationMode={pagination ? (paginationMode || "server") : undefined}
        sortingMode={sortingMode || "server"}
        sortingOrder={sortingOrder || ["desc", "asc"]}
        onPaginationModelChange={pagination ? ((newValue: IPagination) => dispatch(setPaginationModel({ [tableId]: newValue }))) : undefined}
        onSortModelChange={onSortModelChange}
        onRowClick={handleRowClick}
        slots={slots}
        slotProps={{
          ...slotProps,
          ...columnMenuProps,
          pagination: {
            ActionsComponent: TablePaginationActions, // add table pagination with first/last page navigation
            SelectProps: {
              MenuProps: {
                sx: { zIndex: 1500 },
              },
            },
          },
          baseCheckbox: {
            icon: <ShadcnUncheckedIcon />,
            checkedIcon: <ShadcnCheckedIcon />,
            indeterminateIcon: <ShadcnIndeterminateIcon />,
          },
        }}
        initialState={{
          pagination: {
            paginationModel: initialState.pagination,
          },
          pinnedColumns,
          sorting: initialSortModel ? { sortModel: initialSortModel } : undefined,
        }}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        hideFooter={hideFooter}
        onRowDoubleClick={handleRowDoubleClick}
        isRowSelectable={isRowSelectable}
        disableMultipleRowSelection={disableMultipleRowSelection}
        onCellClick={handleCellClick}
        getRowClassName={getRowClassName}
      />
    </div>
  );
};

export default CustomDataGrid;
